import { useContext, useMemo } from 'react';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { CustomizeRequest, NewCarsVehicle } from '~/contracts/new-cars';
import useMatchingVehiclesData from './useMatchingVehiclesData';

export type DeltaCategory = keyof Pick<
  NewCarsVehicle,
  'driveType' | 'engine' | 'exteriorColor' | 'interiorColor' | 'packageOptions'
>;

export type VehicleDelta = {
  category: DeltaCategory;
  name: string;
  priceAdd: number;
};

export type MatchingVehicleWithDeltas = NewCarsVehicle & {
  deltas: VehicleDelta[];
  totalAdded: number;
};

function getDeltas(
  vehicle: NewCarsVehicle,
  selectedOptions: CustomizeRequest | null
): VehicleDelta[] {
  const deltas: VehicleDelta[] = [];

  if (
    vehicle.driveType &&
    vehicle.driveType.name !== selectedOptions?.driveType
  ) {
    const { name, priceAdd } = vehicle.driveType;
    deltas.push({ category: 'driveType', name, priceAdd });
  }

  if (vehicle.engine && vehicle.engine.code !== selectedOptions?.engineCode) {
    const { name, priceAdd } = vehicle.engine;
    deltas.push({ category: 'engine', name, priceAdd });
  }

  if (
    vehicle.exteriorColor &&
    vehicle.exteriorColor.code !== selectedOptions?.exteriorColorCode
  ) {
    const { name, priceAdd } = vehicle.exteriorColor;
    deltas.push({
      category: 'exteriorColor',
      name: `${name} exterior`,
      priceAdd
    });
  }

  if (
    vehicle.interiorColor &&
    vehicle.interiorColor.code !== selectedOptions?.interiorColorCode
  ) {
    const { name, priceAdd } = vehicle.interiorColor;
    deltas.push({
      category: 'interiorColor',
      name: `${name} interior`,
      priceAdd
    });
  }

  vehicle.packageOptions.forEach(({ code, name, priceAdd }) => {
    if (!selectedOptions?.packageOptionCodes?.includes(code)) {
      deltas.push({ category: 'packageOptions', name, priceAdd });
    }
  });

  return deltas.sort((a, b) => b.priceAdd - a.priceAdd);
}

export default function useMatchingVehiclesWithDeltas(): MatchingVehicleWithDeltas[] {
  const { selectedOptions } = useContext(ParentModelContext);
  const { matchingVehicles } = useMatchingVehiclesData();

  return useMemo(
    () =>
      matchingVehicles.map(vehicle => {
        const deltas = getDeltas(vehicle, selectedOptions);
        const totalAdded = deltas.reduce(
          (sum, delta) => sum + delta.priceAdd,
          0
        );

        return { ...vehicle, deltas, totalAdded };
      }),
    [matchingVehicles, selectedOptions]
  );
}
